// ─────────────────────────────────────────────
//  TimelineMaintenanceCard — the two board-setup flows, reachable from the
//  Timeline Chart.
//
//  ⚠️ THIS IS A LAUNCHER, NOT A FEATURE. Both buttons open the OR Board's OWN
//  modals (`CrossFacilityPicker` and `AssignmentModal`), mounted by
//  TimelineChartPage exactly as BoardPage mounts them. Nothing here writes, and
//  nothing here re-implements a placement or a roster append — a second
//  add-assignment path would be a second set of rules that can disagree with the
//  board's, on a write that puts a named person into a named room.
//
//  Purely presentational — the parent owns the modals and every value arrives
//  as a prop.
//
//  ⚠️ NO ALARM COLOURS. Red and blink on this board mean an emergency in a room.
//  Spending them on "add a provider" is what teaches staff to ignore the colour
//  when it finally means something. The buttons are the app's navy, outlined,
//  the same treatment the NEEDS YOU NOW "Fix" affordance uses.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

export default function TimelineMaintenanceCard({
  // The SAME gate the OR Board's admin strip uses (canManageAssignments). A
  // read-only viewer gets NOTHING — not a disabled card, not a greyed strip —
  // exactly as the board hides its banner rather than showing dead controls.
  canManage = false,
  onAddProvider,
  onAddAssignment,
  // Set when the page is showing a day these flows cannot honestly write to.
  // ⚠️ AssignmentModal stamps its row with `rosterDateKey()` — TODAY — and
  // ignores whatever date the caller is browsing (AssignmentModal.js:162). On
  // the OR Board that is invisible, because the board IS today. This page has a
  // free date picker, so the same click on 20 Aug would silently write a real
  // assignment onto today's live board. Named rather than merely disabled, so
  // the operator knows what to change (same treatment as the task composer's
  // `composeBlockedReason` on this page).
  blockedReason = null,
}) {
  if (!canManage) return null;

  const blocked = Boolean(blockedReason);
  const noteId = "timeline-maintenance-blocked";

  return (
    <section style={s.card} data-testid="maintenance-card">
      <header style={s.cardHead}>
        <h2 style={s.cardTitle}>MAINTENANCE</h2>
        <span style={s.cardNote}>the board's own flows</span>
      </header>

      <div style={s.btns}>
        <button
          type="button"
          style={{ ...s.btn, ...(blocked ? s.btnOff : null) }}
          onClick={onAddProvider}
          disabled={blocked}
          aria-describedby={blocked ? noteId : undefined}
          title="Pull a CRNA or a doctor onto today's roster"
        >
          Add a provider
        </button>
        <button
          type="button"
          style={{ ...s.btn, ...(blocked ? s.btnOff : null) }}
          onClick={onAddAssignment}
          disabled={blocked}
          aria-describedby={blocked ? noteId : undefined}
          title="Place a CRNA and a supervising doctor into a room"
        >
          Add assignment
        </button>
      </div>

      {blocked && (
        <p id={noteId} style={s.blocked}>
          {blockedReason}
        </p>
      )}
    </section>
  );
}

const s = {
  card: {
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.lg,
    padding: "11px 13px 13px",
    minWidth: 0,
    boxShadow: tokens.shadow.sm,
  },
  cardHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 9,
    paddingBottom: 7,
    borderBottom: `1px solid ${color.border.default}`,
  },
  cardTitle: {
    margin: 0,
    fontSize: 11,
    fontWeight: font.weight.bold,
    letterSpacing: ".9px",
    color: color.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  cardNote: { fontSize: 10.5, color: color.text.muted, whiteSpace: "nowrap" },

  // Stacked, not side by side. This card lives in BOUNDARY OUTLOOK's track,
  // which is ~220px at the narrow end — two buttons across would each be under
  // 100px and wrap their own labels.
  btns: { display: "flex", flexDirection: "column", gap: 7 },
  // 12px on a BUTTON is fine (the 16px floor is about focusable TEXT INPUTS,
  // which iOS zooms into). 34px tall keeps the pair comfortably tappable
  // without turning a reference column into a toolbar.
  btn: {
    display: "block",
    width: "100%",
    minHeight: 34,
    fontSize: 12,
    fontWeight: font.weight.bold,
    fontFamily: "inherit",
    letterSpacing: ".2px",
    color: color.accent.primary,
    background: color.surface.card,
    border: `1px solid ${color.accent.primary}`,
    borderRadius: radius.sm,
    padding: "8px 10px",
    cursor: "pointer",
    textAlign: "center",
  },
  btnOff: {
    color: color.text.muted,
    borderColor: color.border.default,
    background: color.surface.base,
    cursor: "not-allowed",
  },
  // Amber, not red: this is "not from here", not "something is wrong". #7a4f00
  // is the AA ink for that ground (2.15:1 raw amber as text fails outright).
  blocked: {
    margin: "9px 0 0",
    fontSize: 10.5,
    lineHeight: 1.45,
    color: "#7a4f00",
    background: "#fef8ec",
    border: `1px solid ${color.accent.closing}`,
    borderRadius: radius.sm,
    padding: "7px 9px",
  },
};
